/**
 * Imputación on a phone (<640 px): one card per day instead of the month grid.
 *
 * It also holds the pieces the desktop grid shares with it — the editable cell
 * and the day chip — so both views apply the same rules (ghosts, floor, absence
 * cover, result marks) from one place. ImputacionGrid imports them from here.
 */
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import type { ImputacionDay, ImputacionLine, ImputacionMonth, IsoDate, SubmitResult } from '../../../types/imputacion'
import {
    type CellIssue,
    type Draft,
    DAY_TOLERANCE,
    blockingLines,
    cellValue,
    dayCoveredByAbsence,
    dayFillHours,
    dayTotal,
    ddmm,
    dayLabel,
    fmtH,
    fmtNum,
    fmtSigned,
    hasDraft,
    orderedLines,
    parseHours,
    showGhost,
    validateCell,
    visibleDays,
} from './imputacionLogic'

export interface GridProps {
    month: ImputacionMonth
    draft: Draft
    readOnly: boolean
    onCellChange: (lineKey: string, iso: IsoDate, value: number | null) => void
    onFillDay: (iso: IsoDate) => void
    /** Last submit's results, by `${lineKey}|${iso}`. */
    results: Record<string, SubmitResult>
    /** Days that changed in Redmine during the last submit. */
    conflictDays?: Set<IsoDate>
    /** Day to scroll to and highlight ("Ir al primero"). */
    highlightDay?: IsoDate | null
    /**
     * A submit is running: inputs are frozen. The run sends the cells it took
     * at the start and then rewrites the draft from the re-read, so an edit
     * typed meanwhile would be silently lost (or half-sent).
     */
    locked?: boolean
}

export const cellDomId = (lineKey: string, iso: IsoDate) => `imp-cell-${lineKey}-${iso}`
export const dayDomId = (iso: IsoDate) => `imp-day-${iso}`

/** Whether a cell takes input at all (the person's own, editable month and day, on a loggable line). */
export function isCellEditable(month: ImputacionMonth, line: ImputacionLine, day: ImputacionDay, readOnly: boolean) {
    return !readOnly && month.can_edit && day.editable && line.loggable && line.redmine_project_id !== null
}

/**
 * The day can still get the recommendation (header "Completar" link). Not
 * while a line the day needs is still waiting for a project: the fill would
 * leave that share out and the day would stay short without saying why (the
 * main button explains it instead).
 */
export function canFillDay(month: ImputacionMonth, day: ImputacionDay, readOnly: boolean) {
    return (
        !readOnly &&
        month.can_edit &&
        month.bulk_fill_allowed &&
        day.editable &&
        (day.chip === 'parcial' || day.chip === 'vacio') &&
        dayFillHours(month, day.date) > DAY_TOLERANCE &&
        blockingLines(month, [day.date]).length === 0
    )
}

// ---------------------------------------------------------------------------
// Day chip
// ---------------------------------------------------------------------------

type Tone = 'ok' | 'warn' | 'bad' | 'muted' | 'info'

const TONES: Record<Tone, { bg: string; fg: string }> = {
    ok: { bg: '#E8F5E9', fg: '#2E7D32' },
    warn: { bg: '#FFF3E0', fg: '#E65100' },
    bad: { bg: '#FFEBEE', fg: '#C62828' },
    muted: { bg: '#F0F0F0', fg: '#555' },
    info: { bg: '#E3F2FD', fg: '#1565C0' },
}

export const Chip = styled.span<{ $tone: Tone }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $tone }) => TONES[$tone].bg};
  color: ${({ $tone }) => TONES[$tone].fg};
`

/** Icon plus text for a day's state; null when the day has nothing to say (future). */
export function dayChipInfo(day: ImputacionDay, conflict: boolean): { text: string; tone: Tone } | null {
    if (conflict) return { text: '⟳ Revisar', tone: 'warn' }
    switch (day.chip) {
        case 'completo':
            return { text: 'Completo ✓', tone: 'ok' }
        case 'cubierto':
            return { text: 'Vacaciones', tone: 'info' }
        case 'parcial':
            return { text: `! Faltan ${fmtH(day.missing_hours)}`, tone: 'warn' }
        case 'vacio':
            return { text: 'Sin imputar', tone: 'bad' }
        case 'festivo':
            return { text: 'Festivo', tone: 'muted' }
        default:
            return null
    }
}

export function DayChip({ day, conflict }: { day: ImputacionDay; conflict: boolean }) {
    const info = dayChipInfo(day, conflict)
    if (!info) return null
    return <Chip $tone={info.tone}>{info.text}</Chip>
}

/** "✓" / "! −2,5" / "▲ +6" for the day total against its expected hours. */
export function dayTotalInfo(total: number, expected: number): { text: string; title?: string; tone: Tone } {
    const diff = total - expected
    if (Math.abs(diff) <= DAY_TOLERANCE) return { text: '✓', tone: 'ok' }
    if (diff < 0) return { text: `! ${fmtSigned(diff)}`, tone: 'warn' }
    return { text: `▲ ${fmtSigned(diff)}`, title: `Más horas que la jornada (${fmtH(expected)})`, tone: 'warn' }
}

// ---------------------------------------------------------------------------
// Cell
// ---------------------------------------------------------------------------

const cellBox = css`
  width: 100%;
  min-width: 44px;
  box-sizing: border-box;
  padding: 4px 4px;
  font-size: 0.8rem;
  text-align: center;
`

const Input = styled.input<{ $draft: boolean; $invalid: boolean }>`
  ${cellBox}
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: white;
  ${({ $draft }) => $draft && css`border: 2px solid #1976D2; background: #F5F9FF;`}
  ${({ $invalid }) => $invalid && css`border: 2px solid #C62828; background: #FFF5F5;`}

  &::placeholder {
    color: #9E9E9E;
    font-style: italic;
  }

  &:disabled {
    background: #F2F2F2;
    color: #9E9E9E;
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 0;
  }
`

const StaticValue = styled.span<{ $ghost?: boolean }>`
  ${cellBox}
  display: inline-block;
  color: ${({ $ghost, theme }) => ($ghost ? '#9E9E9E' : theme.colors.text)};
  font-style: ${({ $ghost }) => ($ghost ? 'italic' : 'normal')};
  font-weight: ${({ $ghost }) => ($ghost ? 400 : 600)};
`

const TakeHint = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 0.66rem;
  color: #1565C0;
  cursor: pointer;
  text-decoration: underline dotted;
`

const ResultMark = styled.span<{ $ok: boolean }>`
  font-size: 0.7rem;
  font-weight: 700;
  margin-left: 2px;
  color: ${({ $ok }) => ($ok ? '#2E7D32' : '#E65100')};
  cursor: default;
`

const CellWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`

const OK_STATUSES = new Set(['created', 'updated', 'deleted', 'unchanged'])

export interface CellInputProps {
    month: ImputacionMonth
    line: ImputacionLine
    day: ImputacionDay
    draft: Draft
    readOnly: boolean
    onChange: (lineKey: string, iso: IsoDate, value: number | null) => void
    result?: SubmitResult
    locked?: boolean
}

/**
 * One (line, day) cell. Typing keeps local text until blur/Enter, so "8," can be
 * typed on the way to "8,5" without the draft flickering through invalid values.
 */
export function CellInput({ month, line, day, draft, readOnly, onChange, result, locked = false }: CellInputProps) {
    const iso = day.date
    const logged = line.logged[iso] ?? 0
    const suggested = line.suggested[iso] ?? 0
    const inDraft = hasDraft(draft, line.key, iso)
    const value = cellValue(line, iso, draft)
    const display = inDraft || logged > 0 ? fmtNum(value) : ''
    const ghost = showGhost(line, day, draft)
    const [text, setText] = useState(display)

    useEffect(() => setText(display), [display])

    const editable = isCellEditable(month, line, day, readOnly)
    const covered = line.kind !== 'absence' && dayCoveredByAbsence(month, iso, draft)
    const issue: CellIssue | null = inDraft ? validateCell(line, iso, value) : null
    const mark = result ? (
        <ResultMark
            $ok={OK_STATUSES.has(result.status)}
            tabIndex={0}
            role="img"
            aria-label={result.message ?? result.status}
            title={result.message ?? undefined}
        >
            {OK_STATUSES.has(result.status) ? '✓' : '⚠'}
        </ResultMark>
    ) : null

    if (!editable) {
        if (logged > 0) {
            return (
                <CellWrap>
                    <StaticValue>{fmtNum(logged)}{mark}</StaticValue>
                </CellWrap>
            )
        }
        if (ghost) {
            return (
                <CellWrap>
                    <StaticValue
                        $ghost
                        title={day.state === 'future' ? 'Previsión: aún no se puede imputar' : 'Recomendado'}
                    >
                        {fmtNum(suggested)}
                    </StaticValue>
                </CellWrap>
            )
        }
        // A line still waiting for a project shows its plan greyed, with no input.
        const planned = line.recommended[iso] ?? 0
        if (line.kind === 'needs_choice' && planned > 0) {
            return (
                <CellWrap>
                    <StaticValue $ghost title="Elige antes en qué proyecto imputarlo">
                        {fmtNum(planned)}
                    </StaticValue>
                </CellWrap>
            )
        }
        return <CellWrap>{mark}</CellWrap>
    }

    const commit = (raw: string) => {
        const parsed = parseHours(raw)
        if (parsed === null) {
            // Clearing a logged cell means "remove it"; clearing an empty one just drops the draft.
            onChange(line.key, iso, logged > 0 ? 0 : null)
            return
        }
        if (Number.isNaN(parsed)) {
            setText(display)
            return
        }
        onChange(line.key, iso, parsed)
    }

    return (
        <CellWrap>
            <Input
                id={cellDomId(line.key, iso)}
                type="text"
                inputMode="decimal"
                aria-label={`${line.label} ${ddmm(iso)}`}
                aria-invalid={issue ? true : undefined}
                title={covered ? 'Día cubierto por vacaciones' : issue?.message}
                disabled={covered || locked}
                $draft={inDraft}
                $invalid={Boolean(issue)}
                value={covered ? '' : text}
                placeholder={ghost && logged === 0 ? fmtNum(suggested) : ''}
                onChange={(e) => setText(e.target.value)}
                onBlur={() => {
                    if (text !== display) commit(text)
                }}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    if (text.trim() === '' && ghost) onChange(line.key, iso, suggested)
                    else commit(text)
                }}
            />
            {ghost && logged > 0 && !covered && !locked && (
                <TakeHint type="button" onClick={() => onChange(line.key, iso, suggested)} title="Usar lo recomendado">
                    → {fmtNum(suggested)}
                </TakeHint>
            )}
            {mark}
        </CellWrap>
    )
}

// ---------------------------------------------------------------------------
// Mobile list
// ---------------------------------------------------------------------------

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const Toggle = styled.button`
  align-self: flex-start;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 6px 12px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
`

const Card = styled.div<{ $highlight: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: white;
  ${({ $highlight }) => $highlight && css`box-shadow: 0 0 0 3px #FFB300;`}
`

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const LineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const CardFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const SmallButton = styled.button`
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  cursor: pointer;
`

export function DayCardList({
    month,
    draft,
    readOnly,
    onCellChange,
    onFillDay,
    results,
    conflictDays,
    highlightDay,
    locked = false,
}: GridProps) {
    const [showAll, setShowAll] = useState(false)
    const lines = orderedLines(month)
    const days = visibleDays(month)
    const isPending = (day: ImputacionDay) =>
        (day.editable && (day.chip === 'parcial' || day.chip === 'vacio')) ||
        lines.some((l) => hasDraft(draft, l.key, day.date)) ||
        Boolean(conflictDays?.has(day.date))
    const shown = showAll ? days : days.filter(isPending)

    return (
        <List>
            <Toggle type="button" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Ver solo los días pendientes' : 'Ver todos los días'}
            </Toggle>
            {shown.length === 0 && <div>No tienes días pendientes ✓</div>}
            {shown.map((day) => {
                const total = dayTotal(month, day.date, draft)
                const totalInfo = day.state !== 'future' && day.expected_hours > 0 ? dayTotalInfo(total, day.expected_hours) : null
                return (
                    <Card key={day.date} id={dayDomId(day.date)} $highlight={highlightDay === day.date}>
                        <CardHead>
                            <span>
                                {dayLabel(day.date)}/{day.date.slice(5, 7)} · {fmtH(day.expected_hours)}
                                {day.kind === 'weekend' && ' · no laborable'}
                            </span>
                            <DayChip day={day} conflict={Boolean(conflictDays?.has(day.date))} />
                        </CardHead>
                        {lines.map((line) => (
                            <LineRow key={line.key}>
                                <span>{line.label}</span>
                                <CellInput
                                    month={month}
                                    line={line}
                                    day={day}
                                    draft={draft}
                                    readOnly={readOnly}
                                    onChange={onCellChange}
                                    result={results[`${line.key}|${day.date}`]}
                                    locked={locked}
                                />
                            </LineRow>
                        ))}
                        <CardFoot>
                            <span title={totalInfo?.title}>
                                Total {fmtH(total)} {totalInfo?.text}
                            </span>
                            {!locked && canFillDay(month, day, readOnly) && (
                                <SmallButton type="button" onClick={() => onFillDay(day.date)}>
                                    Completar día
                                </SmallButton>
                            )}
                        </CardFoot>
                    </Card>
                )
            })}
        </List>
    )
}
